import { formatColList, formatRowRange } from '@/components/lake/extraction-modal/helpers'
import { Label } from '@/components/ui/label'
import type { TabularColumn } from '@/services/api/lake'

export interface ReadOnlyDataSidebarProps {
  headerNames: string[]
  headerRowIndex: number | null
  scopeLoadError: string | null
  pendingRows: Set<number>
  pendingColumns: Set<number>
  dataCols: TabularColumn[]
  readOnlyCaseName: string
  scopeNote: string
}

/**
 * Pure read-only data sidebar shown alongside the data preview in readOnly mode.
 * Summarizes the saved headers, the selected rectangular region, and the scope
 * metadata (case + note).
 */
export function ReadOnlyDataSidebar({
  headerNames,
  headerRowIndex,
  scopeLoadError,
  pendingRows,
  pendingColumns,
  dataCols,
  readOnlyCaseName,
  scopeNote,
}: ReadOnlyDataSidebarProps) {
  return (
    <div className="w-64 flex flex-col overflow-hidden bg-muted/20 max-md:w-full max-md:shrink-0 max-md:overflow-visible max-md:border-t">
      <div className="flex-1 overflow-y-auto max-md:flex-none max-md:overflow-visible">
        {/* HEADERS */}
        <div className="p-3 border-b">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
            Headers
          </span>
          {scopeLoadError ? (
            <p className="text-xs text-destructive">{scopeLoadError}</p>
          ) : headerNames.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">—</p>
          ) : (
            <div className="space-y-1">
              {headerNames.map((name, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}</span>
                  <span className="text-xs text-foreground">
                    {name || <em className="text-muted-foreground">(empty)</em>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {headerRowIndex !== null && (
            <p className="text-xs text-muted-foreground mt-2">
              Header from row <strong>{headerRowIndex}</strong>
            </p>
          )}
          {headerRowIndex === null && headerNames.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">Manually entered headers</p>
          )}
        </div>

        {/* DATA SELECTION */}
        <div className="p-3 border-b">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
            Data Selection
          </span>
          {pendingRows.size === 0 && pendingColumns.size === 0 ? (
            <p className="text-xs text-muted-foreground italic">No selection saved</p>
          ) : (
            <div className="text-xs space-y-1">
              <div className="text-muted-foreground">{formatRowRange([...pendingRows])}</div>
              <div className="text-muted-foreground truncate">
                {formatColList([...pendingColumns], dataCols)}
              </div>
              <div className="font-medium text-foreground pt-0.5">
                {pendingRows.size} row{pendingRows.size !== 1 ? 's' : ''} × {pendingColumns.size}{' '}
                col
                {pendingColumns.size !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>

        {/* SCOPE INFO */}
        <div className="p-3 space-y-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
            Scope Info
          </span>
          <div>
            <Label className="text-xs mb-1 block text-muted-foreground">Case</Label>
            <p className="text-xs text-foreground">{readOnlyCaseName || '—'}</p>
          </div>
          {scopeNote && (
            <div>
              <Label className="text-xs mb-1 block text-muted-foreground">Note</Label>
              <p className="text-xs text-foreground">{scopeNote}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
